import Paging from '../../core/Paging';
import { Typography } from '@material-ui/core';
import DescriptionIcon from '@material-ui/icons/Description';
import PropTypes from 'prop-types';
import React from 'react';
import CommonIriDisplay from '../../application-core/CommonIriDisplay';
import Errored from '../../core/Errored';
import Loading from '../../core/Loading';
import Notification from '../../core/Notification';
import TitledPanel from '../../core/TitledPanel';
import { getGraphFromProject, getProjectFromGraph, makeCancelable, sortByLangPropList } from '../../core/utils/utils';
import GS from '../../grafsync/grafsync';

// Changed to {} to match CommonIRIdisplay
const loadOptions = {};
const PAGE_SIZE = 100;
const MAX_NODES = 5000;

class OntologyRelationshipsDisplay extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      page: 1,
      initialLoading: props.values ? props.values.length > 0 : false,
      isLoading: props.values ? props.values.length > 0 : false,
      totalRelations: [],
      relatedResources: undefined,
      unloadedResources: [],
      isErrored: undefined
    };
    this.cancellable = null;
  }

  static equals(a, b) {
    return a.length === b.length && a.every((v, i) => v === b[i]);
  }

  static getDerivedStateFromProps(props, state) {
    const changed = props.viewLang !== state.sortLang ?
      true
      :
      props.values ?
        !OntologyRelationshipsDisplay.equals(props.values, state.totalRelations)
        :
        false;
    if (!state.isLoading && props.values && changed) {
      // If a deletion happens that reduces page count and we're on the last page we reset page to page count
      const pageCount = Math.floor((props.values.length + PAGE_SIZE - 1) / PAGE_SIZE);
      let page = state.page;
      if (page > pageCount && pageCount > 0) page = pageCount;
      else if (page > pageCount && pageCount === 0) page = 1;
      return {
        relatedResources: undefined,
        isLoading: props.values && props.values.length > 0,
        sortLang: props.viewLang,
        page,
        totalRelations: [...props.values],
        unloadedResources: []
      };
    }
    return null;
  }

  componentDidUpdate() {
    if (!this.state.isErrored && this.state.relatedResources === undefined
      && this.props.values && this.props.values.length > 0) {
      this.collectResourceData();
    }
  }

  componentDidMount() {
    if (this.props.values && this.props.values.length > 0) {
      this.collectResourceData();
    }
    if (this.props.searchClasses.length > 0 && this.props.associatedProjectGraph) {
      this.setState({ isSearching: true }, () => {
        this.setState({
          sortLang: this.props.viewLang,
          isSearching: false
        });
      });
    }
  }

  loadData(iris, options, graph) {
    if (!graph) graph = this.props.graph;
    this.cancellable = makeCancelable(GS.loadData(iris, options, graph, true));
    return this.cancellable;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
  }

  async collectResourceData() {
    if (this.props.values && this.props.values.length > 0) {
      try {
        // At some point we want to filter the properties loaded for each component
        // We load ALL the concepts necessary at this level in the tree in one API call to avoid unnecessary calls
        GS.handle("value-check");
        if (this.props.values && this.props.values[0] === undefined) throw new Error("There is an issue with the data for this resource.");
        // We try and load everything from the same project to start with
        const pages = Math.floor((this.props.values.length + MAX_NODES - 1) / MAX_NODES);
        for (let page = 0; page < pages; page++) {
          const pageStart = page * MAX_NODES;
          const pageEnd = (page + 1) * MAX_NODES;
          const pageChildren = this.props.values.slice(pageStart, pageEnd);
          await this.loadData(pageChildren, loadOptions).promise;
          if (this.cancellable && this.cancellable.isCancelled()) return;
        }
        if (this.cancellable && this.cancellable.isCancelled()) return;
        const linkedProjects = this.props.associatedProject ? [...this.props.associatedProject].sort() : [];
        // Then we get a list of what is not in this graph
        let doesNotExist = this.props.values.filter(iri => !GS.isLoaded(iri, this.props.graph));
        let allDoesNotExist = [...doesNotExist];
        let lpCounter = 0;
        let graphInfo = {};
        // Then we search linked projects for these resources
        while (doesNotExist.length > 0 && lpCounter < linkedProjects.length) {
          const linkedGraph = getGraphFromProject(linkedProjects[lpCounter]);
          // We also load the linked project too
          await this.loadData([...doesNotExist, linkedProjects[lpCounter]], loadOptions, linkedGraph).promise;
          if (this.cancellable && this.cancellable.isCancelled()) return;
          const doesNowExist = doesNotExist.filter(iri => GS.isLoaded(iri, linkedGraph));
          doesNowExist.forEach(iri => graphInfo[iri] = linkedGraph);
          doesNotExist = doesNotExist.filter(iri => !GS.isLoaded(iri, linkedGraph));
          lpCounter++;
        }
        if (this.cancellable && this.cancellable.isCancelled()) return;
        this.setState({ unloadedResources: allDoesNotExist, graphInfo });
        this.processResources();
        this.setState({ isLoading: false, initialLoading: false });
      }
      catch (e) {
        if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
      }
    }
    else {
      this.setState({ isLoading: false, initialLoading: false });
    }
  }

  processResources() {
    try {
      // Not all resources related to may be loaded (or may not be allowed through lack of permissions)
      //const doesNotExist = this.props.values.filter(iri => !GS.isLoaded(iri, this.props.graph));
      let graphInfo = this.state.graphInfo;
      let resources = this.props.values
        .map(id => {
          const graph = graphInfo[id] || this.props.graph;
          return GS.isLoaded(id, graph) ?
            GS.data(id, graph)
            :
            {
              id,
              notFound: true
            };
        });
      const rr = sortByLangPropList([...resources], ["prefLabel", "literalForm", "label", "title"], this.props.viewLang, this.props.defaultLang);
      if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ relatedResources: rr });
    }
    catch (e) {
      console.log("Cannot load associated resources");// eslint-disable-line
    }
  }

  monitorLinkUpdate = (iri, property) => {
    if (property === "prefLabel" || property === "label" || property === "literalForm") this.processResources();
  };

  handlePageChange = async (page = 1) => this.setState({ page });

  render() {

    const { components, isReadOnly, defaultLang, associatedProjectGraph, sourceComponent, dialog, searchTypes, range,
      searchClasses, viewLang, graph, cardinalityAtMax, linkedData, deleted, deprecated, exceedsCardinality, info, values, allowIRI, useWorkflow,
      title, projectIRI, matching, includeExistingResource, resourceIRI, property, canAddValues, associatedProject,
      applicableStatements, reify, onReify, suppressReason, exclude, information, inferSubclasses } = this.props;
    const { relatedResources, unloadedResources, graphInfo, isErrored, initialLoading, page } = this.state;
    const exclusionList = relatedResources ? relatedResources.map(rr => rr.id) : [];

    if (isErrored) return <Errored dataTest="or-error" data={isErrored} />;

    const FindDialog = dialog;
    const fullExclude = [...new Set([...exclusionList, ...unloadedResources, resourceIRI, ...exclude])];

    const pageCount = relatedResources ? Math.floor((relatedResources.length + PAGE_SIZE - 1) / PAGE_SIZE) : 0;
    const startNode = (page - 1) * PAGE_SIZE;

    return (
      <TitledPanel
        warning={exceedsCardinality ? "Too many relationships. Only " + (info.maxCount || 1) + " expected." : ""}
        dataTest={"property-" + title}
        height="full"
        title={info.name || title}
        information={information}
        actions=
        {(!isReadOnly && !initialLoading && !cardinalityAtMax && !suppressReason && canAddValues && !deleted && !deprecated && !info.inComponentConflict && !info.inComponentProjectConflict) ?
          <FindDialog
            source={resourceIRI}
            linkedData={info.inComponent ? [] : linkedData}
            useWorkflow={useWorkflow}
            info={info}
            property={property}
            range={range}
            inferSubclasses={inferSubclasses}
            searchTypes={searchTypes}
            sourceComponent={sourceComponent}
            associatedProject={info.inComponent ? [] : associatedProject}
            associatedProjectGraph={associatedProjectGraph}
            searchClasses={[...searchClasses]}
            projectIRI={projectIRI}
            graph={graph}
            allowIRI={allowIRI}
            matching={matching}
            viewLang={viewLang}
            defaultLang={defaultLang}
            exclude={fullExclude}
            onIncludeResource={includeExistingResource} />
          :
          null}>
        {info.description &&
          <div style={{ marginBottom: 12 }}>
            <Typography variant="body2"><DescriptionIcon style={{ transform: 'translateY(4px)' }} fontSize="small" /> <span>{info.description}</span></Typography>
          </div>
        }
        {suppressReason && <Notification dataTest="suppressed-property" error={false} text={suppressReason} />}
        {info.minCount && values.length < info.minCount && <Notification dataTest="warning" error={false} text={"Min. relationships required: " + info.minCount} />}
        {info.inComponentProjectConflict && <Notification dataTest="select-from-project-conflict" error={false} text={"Select From values from multiple projects are conflicting. Please fix."} />}
        {info.inComponentConflict && <Notification dataTest="select-from-conflict" error={false} text={"Select From values from multiple templates are conflicting. Please fix."} />}
        {info.minCountConflict && <Notification dataTest="min-count-conflict" error={false} text={"Min. Count values from multiple templates are conflicting. The largest value is being used."} />}
        {info.maxCountConflict && <Notification dataTest="max-count-conflict" error={false} text={"Max. Count values from multiple templates are conflicting. The smallest value is being used."} />}
        {deleted && <Notification error={true} text="This property is pending deletion" dataTest="pending-deletion" />}
        {deprecated && <Notification error={false} text="This property is deprecated" dataTest="deprecated" />}
        {!canAddValues &&
          <Notification error={false} text="This resource no longer has a class valid for this property." dataTest="not-valid" />
        }
        {relatedResources && relatedResources.some(rr => exclude.includes(rr.id)) &&
          <Notification error={false} text="There are clashing values on disjoint properties." dataTest="clashing-values" />
        }
        {initialLoading ? <Loading message={values.length > 1000 ? "There are a lot of resources to load!" : ""} /> : null}
        {pageCount > 1 ?
          <div>
            <Typography data-test="many-relations" variant="caption">There are a total of {relatedResources.length} relationships.</Typography>
            <Paging showText={false} page={page} pageCount={pageCount} handlePageChange={this.handlePageChange} />
          </div>
          :
          null
        }
        {relatedResources && relatedResources
          .slice(startNode, startNode + PAGE_SIZE)
          .map(rr => {
            if (rr.notFound || graphInfo[rr.id]) {
              return <CommonIriDisplay
                id={rr.id}
                key={rr.id}
                components={graphInfo[rr.id] ? undefined : components}
                projectIRI={graphInfo[rr.id] ? getProjectFromGraph(graphInfo[rr.id]) : projectIRI}
                loadComponents={graphInfo[rr.id] ? true : false}
                reify={reify}
                onReify={onReify}
                applicableStatements={applicableStatements}
                otherComponent={false}
                handleNotAvailable={true}
                property={property}
                useWorkflow={useWorkflow}
                propertyGraph={associatedProjectGraph}
                isReadOnly={isReadOnly}
                compact={true}
                graph={graphInfo[rr.id] || graph}
                sourceGraph={graph}
                source={resourceIRI}
                updateEvent={this.monitorLinkUpdate}
                wrap={false}
                viewLang={viewLang}
                defaultLang={defaultLang} />;
            }
            else {
              if ((rr.inScheme && rr.inScheme.length > 0)
                || (rr.inDataset && rr.inDataset.length > 0)
                || rr.visibility
                || rr.isDefinedBy) {
                // Is link within components in project?
                const isProjectLink = rr.inScheme ?
                  components && rr.inScheme.some(sIRI => components.includes(sIRI))
                  :
                  rr.inDataset ?
                    components && rr.inDataset.some(sIRI => components.includes(sIRI))
                    :
                    components && (Array.isArray(rr.visibility) ?
                      rr.visibility.some(sIRI => components.includes(sIRI))
                      :
                      components.includes(rr.visibility)
                    );
                if (isProjectLink) {
                  return <CommonIriDisplay
                    key={"rel-" + resourceIRI + "-" + rr.id}
                    id={rr.id}
                    expectClass={searchClasses}
                    highlight={exclude.includes(rr.id)}
                    components={components}
                    projectIRI={projectIRI}
                    reify={reify}
                    onReify={onReify}
                    applicableStatements={applicableStatements}
                    otherComponent={false}
                    handleNotAvailable={true}
                    property={property}
                    useWorkflow={useWorkflow}
                    propertyGraph={associatedProjectGraph}
                    isReadOnly={isReadOnly}
                    compact={true}
                    graph={graph}
                    source={resourceIRI}
                    updateEvent={this.monitorLinkUpdate}
                    wrap={false}
                    viewLang={viewLang}
                    defaultLang={defaultLang} />;
                }
              }
              else {
                const rrTypes = Array.isArray(rr.type) ? rr.type : [rr.type];
                if (rrTypes.includes("ConceptScheme")
                  || rrTypes.includes("Collection")
                  || rrTypes.includes("OrderedCollection")
                  || rrTypes.includes("Ontology")
                  || rrTypes.includes("Dataset")) {
                  return <CommonIriDisplay
                    key={rr.id}
                    id={rr.id}
                    components={components}
                    projectIRI={projectIRI}
                    otherComponent={false}
                    handleNotAvailable={true}
                    property={property}
                    expectClass={searchClasses}
                    propertyGraph={associatedProjectGraph}
                    compact={true}
                    graph={graph}
                    source={resourceIRI}
                    updateEvent={this.monitorLinkUpdate}
                    wrap={false}
                    viewLang={viewLang}
                    defaultLang={defaultLang} />;
                }
                else {
                  // Is concept a free floating concept, label or resource?
                  return <CommonIriDisplay
                    key={"rel-" + resourceIRI + "-" + rr.id}
                    id={rr.id}
                    components={components}
                    projectIRI={projectIRI}
                    expectClass={searchClasses}
                    reify={reify}
                    onReify={onReify}
                    applicableStatements={applicableStatements}
                    otherComponent={false}
                    handleNotAvailable={true}
                    property={property}
                    useWorkflow={useWorkflow}
                    propertyGraph={associatedProjectGraph}
                    isReadOnly={isReadOnly}
                    compact={true}
                    graph={graph}
                    source={resourceIRI}
                    updateEvent={this.monitorLinkUpdate}
                    wrap={false}
                    viewLang={viewLang}
                    defaultLang={defaultLang} />;
                }
              }
            }
            // We shouldn't get to this line
            return null;
          })}
      </TitledPanel>
    );
  }
}

OntologyRelationshipsDisplay.defaultProps = {
  canAddValues: true,
  cardinalityAtMax: false,
  useWorkflow: false,
  reify: false,
  associatedProject: [],
  searchTypes: [],
  values: [],
  exclude: []
};

OntologyRelationshipsDisplay.propTypes = {
  dialog: PropTypes.any,
  canAddValues: PropTypes.bool,
  info: PropTypes.object,
  cardinalityAtMax: PropTypes.bool,
  exceedsCardinality: PropTypes.bool,
  useWorkflow: PropTypes.bool,
  allowIRI: PropTypes.bool,
  deleted: PropTypes.bool,
  deprecated: PropTypes.bool,
  isLoading: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  graph: PropTypes.string.isRequired,
  sourceComponent: PropTypes.string,
  resourceIRI: PropTypes.string.isRequired,
  components: PropTypes.array,
  reify: PropTypes.bool,
  onReify: PropTypes.func,
  searchTypes: PropTypes.array,
  defaultLang: PropTypes.string.isRequired,
  viewLang: PropTypes.string,
  range: PropTypes.array,
  linkedData: PropTypes.array,
  suppressReason: PropTypes.string,
  associatedProject: PropTypes.array,
  associatedProjectGraph: PropTypes.string.isRequired,
  searchClasses: PropTypes.array,
  property: PropTypes.string,
  title: PropTypes.string,
  includeExistingResource: PropTypes.func.isRequired,
  values: PropTypes.array.isRequired,
  inferSubclasses: PropTypes.bool,
  exclude: PropTypes.array,
  information: PropTypes.object,
  projectIRI: PropTypes.string.isRequired
};

export default OntologyRelationshipsDisplay;